import { useTranslation } from '@pulsex/localization'
import { Currency, CurrencyAmount } from '@pulsex/swap-sdk-core'
import { NATIVE_USDT } from '@pulsex/tokens'
import { Button, Dots, Message, MessageText, RowBetween } from '@pulsex/uikit'
import { useMemo } from 'react'
import { ApprovalState } from 'hooks/useApproveCallback'
import { Field } from 'state/mint/actions'
import { Address } from 'viem'

interface ApproveLiquidityTokensProps {
  chainId: number
  currencies: {
    [Field.CURRENCY_A]?: Currency
    [Field.CURRENCY_B]?: Currency
  }
  shouldShowApprovalGroup: boolean
  showFieldAApproval: boolean
  approveACallback: () => Promise<{ hash: Address } | undefined>
  revokeACallback: () => Promise<{ hash: Address } | undefined>
  currentAllowanceA: CurrencyAmount<Currency> | undefined
  approvalA: ApprovalState
  showFieldBApproval: boolean
  approveBCallback: () => Promise<{ hash: Address } | undefined>
  revokeBCallback: () => Promise<{ hash: Address } | undefined>
  currentAllowanceB: CurrencyAmount<Currency> | undefined
  approvalB: ApprovalState
}

export default function ApproveLiquidityTokens({
  chainId,
  shouldShowApprovalGroup,
  showFieldAApproval,
  approvalA,
  approveACallback,
  revokeACallback,
  currentAllowanceA,
  currencies,
  showFieldBApproval,
  approvalB,
  approveBCallback,
  revokeBCallback,
  currentAllowanceB,
}: ApproveLiquidityTokensProps) {
  const { t } = useTranslation()

  const revokeANeeded = useMemo(() => {
    return (
      showFieldAApproval &&
      currentAllowanceA?.greaterThan(0) &&
      chainId &&
      currencies[Field.CURRENCY_A]?.chainId === chainId &&
      currencies[Field.CURRENCY_A]?.wrapped.address.toLowerCase() === NATIVE_USDT[chainId].address.toLowerCase()
    )
  }, [showFieldAApproval, currentAllowanceA, currencies, chainId])
  const revokeBNeeded = useMemo(() => {
    return (
      showFieldBApproval &&
      currentAllowanceB?.greaterThan(0) &&
      chainId &&
      currencies[Field.CURRENCY_B]?.chainId === chainId &&
      currencies[Field.CURRENCY_B]?.wrapped.address.toLowerCase() === NATIVE_USDT[chainId].address.toLowerCase()
    )
  }, [showFieldBApproval, currentAllowanceB, currencies, chainId])
  const anyRevokeNeeded = revokeANeeded || revokeBNeeded

  return shouldShowApprovalGroup ? (
    <RowBetween style={{ gap: '12px' }}>
      {anyRevokeNeeded && (
        <Message variant="warning">
          <MessageText>
            <span>
              {t('Native USDT requires resetting approval when spending allowances are too low.')}
            </span>
          </MessageText>
        </Message>
      )}
      {showFieldAApproval &&
        (revokeANeeded ? (
          <Button
            onClick={revokeACallback}
            disabled={approvalA === ApprovalState.PENDING}
            width='100%'
          >
            {approvalA === ApprovalState.PENDING ? (
              <Dots>{t('Reset Approval on USDT', { asset: currencies[Field.CURRENCY_A]?.symbol })}</Dots>
            ) : (
              t('Reset Approval on USDT', { asset: currencies[Field.CURRENCY_A]?.symbol })
            )}
          </Button>
        ) : (
          <Button
            onClick={approveACallback}
            disabled={approvalA === ApprovalState.PENDING}
            width='100%'
          >
            {approvalA === ApprovalState.PENDING ? (
              <Dots>{t('Enabling %asset%', { asset: currencies[Field.CURRENCY_A]?.symbol })}</Dots>
            ) : (
              t('Enable %asset%', { asset: currencies[Field.CURRENCY_A]?.symbol })
            )}
          </Button>
        ))}
      {showFieldBApproval &&
        (revokeBNeeded ? (
          <Button
            onClick={revokeBCallback}
            disabled={approvalB === ApprovalState.PENDING}
            width='100%'
          >
            {approvalB === ApprovalState.PENDING ? (
              <Dots>{t('Reset Approval on USDT', { asset: currencies[Field.CURRENCY_B]?.symbol })}</Dots>
            ) : (
              t('Reset Approval on USDT', { asset: currencies[Field.CURRENCY_B]?.symbol })
            )}
          </Button>
        ) : (
          <Button
            onClick={approveBCallback}
            disabled={approvalB === ApprovalState.PENDING}
            width='100%'
          >
            {approvalB === ApprovalState.PENDING ? (
              <Dots>{t('Enabling %asset%', { asset: currencies[Field.CURRENCY_B]?.symbol })}</Dots>
            ) : (
              t('Enable %asset%', { asset: currencies[Field.CURRENCY_B]?.symbol })
            )}
          </Button>
        ))}
    </RowBetween>
  ) : null
}
